import React, { useState, useEffect, useRef, useCallback } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { getUserData } from "./const";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { createErpSocket } from "../utils/socketIo";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import { ref as dbRef, onValue, set } from "firebase/database";
import { db } from "../firebase/firebase";
import {
  createMetalGroup,
  listMetalGroups,
  updateMetalGroup,
} from "../erpClient";
import { parseApiPagination, extractListRowsFromResponse } from "../utils/parseApiPagination";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";

const initialStatusFilter = "";

function MetalGroupTable() {
  // states
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [metalGroups, setMetalGroups] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Add Metal form states
  const [groupName, setGroupName] = useState("");
  const [purity, setPurity] = useState("");
  const [purityPercent, setPurityPercent] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [metal, setMetal] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [status, setStatus] = useState("active");

  const [socket, setSocket] = useState(null);
  const userData = getUserData();

  const isMounted = useRef(true);

  // Normalize purity for display
  const formatPurityValue = (value) => {
    if (value === null || value === undefined) return "";
    const num = parseFloat(value);
    if (Number.isNaN(num)) return String(value);
    return Number.isInteger(num) ? String(num) : String(num);
  };

  // Single API metal_group record -> table row (used by list, create response, sockets)
  const mapApiMetalGroupToRow = (group) => ({
    id: group.id,
    groupName: group.group_name || "",
    metal:
      (group.metal_type ?? group.metal)
        ? String(group.metal_type ?? group.metal)
            .charAt(0)
            .toUpperCase() +
          String(group.metal_type ?? group.metal)
            .slice(1)
            .toLowerCase()
        : "",
    // UI: Purity (C) -> carat; Purity (%) -> touch (legacy display field)
    purity: formatPurityValue(group.carat ?? group.purity ?? ""),
    touch: formatPurityValue(group.purity ?? group.touch ?? ""),
    hsnCode: group.hsn_code ?? group.HSN_code ?? "",
    status:
      group.status !== null && group.status !== undefined
        ? String(group.status)
        : "1",
    shortCode: group.short_code ? String(group.short_code) : "",
  });

  // ------------------ Parse API records to UI state ------------------
  const parseRecordsToState = (records) => {
    const fetchedGroups = records.map(mapApiMetalGroupToRow);
    fetchedGroups.sort((a, b) => b.id - a.id);
    return fetchedGroups;
  };

  // ------------------ Full fetch used for initial load and after local writes ------------------
  const pageCache = useVisitedPageCache();

  const fetchMetalGroups = useCallback(
    async (page = 1, limit = 10, search = "", force = false) => {
      const cacheKey = visitedPageKey([page, limit, search.trim()]);
      if (force) pageCache.clear();
      const cached = force ? null : pageCache.read(cacheKey);
      if (cached) {
        if (isMounted.current) {
          setMetalGroups(cached.items);
          setTotalCount(cached.total);
          setIsLoading(false);
        }
        return;
      }
      setIsLoading(true);
      try {
        const response = await listMetalGroups({
          search: search.trim(),
          page,
          limit,
        });
        const records = extractListRowsFromResponse(response, [
          "metal_groups",
          "records",
        ]);
        const parsed = parseRecordsToState(records);
        const total = parseApiPagination(response, parsed.length).total;
        pageCache.write(cacheKey, { items: parsed, total });
        if (isMounted.current) {
          setMetalGroups(parsed);
          setTotalCount(total);
        }
      } catch (error) {
        console.error("Error fetching metal groups:", error);
        toast.error(
          error.response?.data?.message || "Failed to fetch metal groups",
        );
        if (isMounted.current) {
          setMetalGroups([]);
          setTotalCount(0);
        }
      } finally {
        if (isMounted.current) setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchMetalGroups(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchMetalGroups]);

  // ✅ Add THIS function here
  // const bumpFirebaseTokenClient = async () => {
  //   try {
  //     const tokenPath = dbRef(db, "updates/metal_group");
  //     await set(tokenPath, String(Date.now()));
  //   } catch (err) {
  //     console.error("Failed to bump Firebase token:", err);
  //   }
  // };

  // ------------------ Update function (keeps your existing API usage) ------------------
  const updateMetalGroups = async (updatedGroup) => {
    try {
      const response = await updateMetalGroup({
        id: updatedGroup.id,
        group_name: updatedGroup.groupName,
        metal_type: updatedGroup.metal || null,
        carat: Number(updatedGroup.purity) || 0,
        purity: Number(updatedGroup.touch) || 0,
        hsn_code: updatedGroup.hsnCode || "",
        short_code: updatedGroup.shortCode || "",
        status: Number(updatedGroup.status) || 0,
      });

      if (response?.success) {
        toast.success("Metal group updated successfully");
        // Optimistically update local state; socket will also update.
        setMetalGroups((prev) =>
          prev
            .map((g) =>
              g.id === updatedGroup.id
                ? {
                    ...g,
                    groupName: updatedGroup.groupName,
                    metal: updatedGroup.metal ?? g.metal,
                    purity: formatPurityValue(updatedGroup.purity ?? g.purity),
                    touch: formatPurityValue(updatedGroup.touch ?? g.touch),
                    hsnCode: updatedGroup.hsnCode,
                    status: String(updatedGroup.status),
                  }
                : g,
            )
            .sort((a, b) => b.id - a.id),
        );
      }
    } catch (error) {
      console.error("Error updating metal group:", error);
      toast.error(
        error.response?.data?.message || "Failed to update metal group",
      );
    }
  };

  // ------------------ Add function (keeps your existing API usage) ------------------
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await createMetalGroup({
        group_name: groupName,
        metal_type: metal || null,
        carat: Number(purity) || 0,
        purity: Number(purityPercent) || 0,
        hsn_code: hsnCode,
        status: status === "active" ? 1 : 0,
      });

      if (response?.success) {
        toast.success("Metal group added successfully");
        setShowAddModal(false);
        setShowSuccessModal(true);
        setTimeout(() => setShowSuccessModal(false), 2000);
        // reset form
        setGroupName("");
        setPurity("");
        setPurityPercent("");
        setHsnCode("");
        setMetal("");
        setShortCode("");
        setStatus("active");
        const created =
          response.data?.metal_group ||
          response.data?.record ||
          (response.data &&
          typeof response.data === "object" &&
          response.data.id != null &&
          !Array.isArray(response.data.metal_groups)
            ? response.data
            : null);

        if (created?.id != null) {
          const next = mapApiMetalGroupToRow(created);
          setMetalGroups((prev) => {
            const exists = prev.some((g) => g.id === next.id);
            const merged = exists
              ? prev.map((g) => (g.id === next.id ? { ...g, ...next } : g))
              : [next, ...prev];
            return merged.sort((a, b) => b.id - a.id);
          });
        } else {
          await fetchMetalGroups(currentPage, itemsPerPage, searchQuery, true);
        }
      } else {
        toast.error(response?.message || "Failed to add metal group");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.message || "Failed to add metal group");
    }
  };

  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in MetalGroupTable, refetching metal groups...",
        event.detail,
      );
      setCurrentPage(1);
      fetchMetalGroups(1, itemsPerPage, searchQuery, true);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, fetchMetalGroups]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ------------------ Robust token listener (debounced + throttled fetch) ------------------
  useEffect(() => {
    const TOKEN_KEY = "metal_group_last_token";
    const DEBOUNCE_MS = 800; // batch quick updates
    const MIN_FETCH_GAP = 1500; // minimum time between actual fetches (ms)

    // start with whatever is stored locally (or "0")
    let lastServerToken = localStorage.getItem(TOKEN_KEY) || "0";
    let lastFetchAt = 0;
    let debounceTimer = null;

    // token path
    const tokenRef = dbRef(db, "updates/metal_group");

    const maybeFetch = async (serverToken) => {
      const now = Date.now();
      if (now - lastFetchAt < MIN_FETCH_GAP) {
        return;
      }
      lastFetchAt = now;
      try {
        await fetchMetalGroups(currentPage, itemsPerPage, searchQuery, true);
      } catch (err) {
        console.error("[TOKEN-LISTENER] fetchMetalGroups error:", err);
      }
    };

    const unsubscribe = onValue(
      tokenRef,
      (snap) => {
        const serverToken = snap.exists() ? snap.val() : null;

        if (!serverToken) return;

        // If token didn't change for this tab, just keep localStorage in sync and skip fetch
        if (serverToken.toString() === lastServerToken) {
          localStorage.setItem(TOKEN_KEY, lastServerToken);
          return;
        }

        // New token detected: update local value, persist, and debounce a fetch
        lastServerToken = serverToken.toString();
        localStorage.setItem(TOKEN_KEY, lastServerToken);

        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(
          () => maybeFetch(lastServerToken),
          DEBOUNCE_MS,
        );
      },
      (err) => {
        console.error("[TOKEN-LISTENER] Firebase listener error:", err);
      },
    );

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------ Socket.IO listener for real-time updates ------------------
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    newSocket.on("connect", () => {
      // console.log("[MetalGroup] Socket connected:", newSocket.id);
    });
    newSocket.on("disconnect", (reason) => {
      // console.log("[MetalGroup] Socket disconnected:", reason);
    });
    newSocket.on("connect_error", (err) => {
      console.error("[MetalGroup] Socket connection error:", err.message);
    });

    newSocket.on("metal_group_updated", (data) => {
      const r = data?.metal_group || data?.record;
      if (!r?.id) return;
      setMetalGroups((prev) =>
        prev.map((group) => {
          if (group.id !== r.id) return group;
          const mergedApi = {
            id: r.id,
            group_name: r.group_name ?? group.groupName,
            metal_type: r.metal_type ?? r.metal ?? group.metal,
            carat: r.carat ?? group.purity,
            purity:
              r.purity !== undefined && r.purity !== null
                ? r.purity
                : group.touch,
            hsn_code: r.hsn_code ?? r.HSN_code ?? group.hsnCode,
            status: r.status != null ? r.status : group.status,
            short_code: r.short_code ?? group.shortCode,
          };
          return mapApiMetalGroupToRow(mergedApi);
        }),
      );
    });

    newSocket.on("metal_group_created", (data) => {
      const r = data?.metal_group || data?.record || data;
      if (!r?.id) return;
      const newGroup = mapApiMetalGroupToRow(r);

      setMetalGroups((prev) => {
        const exists = prev.some((g) => g.id === newGroup.id);
        if (exists) {
          return prev.map((g) => (g.id === newGroup.id ? newGroup : g));
        }
        return [newGroup, ...prev].sort((a, b) => b.id - a.id);
      });
    });

    return () => {
      newSocket.off("metal_group_updated");
      newSocket.off("metal_group_created");
      newSocket.disconnect();
    };
  }, []);

  // ------------------ UI helpers & existing handlers ------------------
  const handleEditClick = (group) => {
    if (!hasPermission(PERMISSIONS.METAL_GROUP_EDIT)) {
      toast.error("You do not have permission to edit metal groups.");
      return;
    }
    // Normalize status to ensure radio buttons work correctly
    // Convert status to string and ensure it's either "0" or "1"
    let normalizedStatus = String(group.status || "1");
    if (normalizedStatus !== "0" && normalizedStatus !== "1") {
      normalizedStatus = "1"; // Default to Active if invalid
    }

    setSelectedGroup({
      ...group,
      status: normalizedStatus,
    });
    setShowEditModal(true);
  };

  const handleSaveChanges = () => {
    updateMetalGroups(selectedGroup);
    setShowEditModal(false);
  };

  // Filtering logic — status filter on current server page
  const filteredGroups = metalGroups.filter((group) => {
    const matchesStatus =
      statusFilter === "" ||
      (statusFilter === "Active" &&
        (group.status === "1" || group.status === 1)) ||
      (statusFilter === "Inactive" &&
        group.status !== "1" &&
        group.status !== 1);

    return matchesStatus;
  });

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const displayedGroups = filteredGroups;

  // Generate short code
  const generateShortCode = (name) =>
    name
      .split(" ")
      .filter((word) => word && !word.includes("-"))
      .map((word) => word.charAt(0).toUpperCase())
      .join("");

  const handleGroupNameChange = (e) => {
    const name = e.target.value;
    setGroupName(name);
    setShortCode(generateShortCode(name));
  };

  const handlePurityChange = (e) => {
    const inputValue = e.target.value;
    if (/^[0-9]*$/.test(inputValue)) setPurity(inputValue);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setGroupName("");
    setPurity("");
    setPurityPercent("");
    setHsnCode("");
    setMetal("");
    setShortCode("");
    setStatus("active");
  };

  // ------------------ Render JSX (kept same as original but uses updated state) ------------------
  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className={`card w-100 ${cardClass}`}>
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Metal Group Table</h4>
            {hasPermission(PERMISSIONS.METAL_GROUP_ADD) && (
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={() => {
                  if (!hasPermission(PERMISSIONS.METAL_GROUP_ADD)) {
                    toast.error("You do not have permission to add metal groups.");
                    return;
                  }
                  setShowAddModal(true);
                }}
              >
                Add Metal Group
              </button>
            )}
          </div>

          <div className="card-body p-4">
            {/* Search and Filter Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <select
                className={`form-select w-auto ms-3 ${buttonClass}`}
                style={{ maxWidth: "200px" }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div
              className={`table-responsive border ${cardClass}`}
              style={{ cursor: "default" }}
            >
              <table
                className={`table text-nowrap align-middle ${tableClass}`}
                style={{ cursor: "default" }}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Group Name</th>
                    <th>Metal</th>
                    <th style={{ cursor: "default" }}>Carat/Purity</th>
                    <th>HSN Code</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedGroups.length > 0 ? (
                    displayedGroups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.groupName}</td>
                        <td>{group.metal}</td>
                        <td>
                          {group.purity}({group.touch})
                        </td>
                        <td>{group.hsnCode}</td>
                        <td>
                          <span
                            className={`badge ${
                              group.status === "1" || group.status === 1
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                            }`}
                          >
                            {group.status === "1" || group.status === 1
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </td>
                        <td>
                          {hasPermission(PERMISSIONS.METAL_GROUP_EDIT) && (
                            <button
                              className={`btn btn-primary btn-sm ${buttonClass}`}
                              onClick={() => handleEditClick(group)}
                            >
                              <Icon
                                icon="solar:pen-bold"
                                width="16"
                                height="16"
                              />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No metal groups found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Metal Group</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ cursor: "default" }}>
          {selectedGroup && (
            <Form>
              <div className="row">
                {/* Group Name */}
                <div className="mb-3 col-md-6">
                  <Form.Label>Group Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedGroup.groupName}
                    className={buttonClass}
                    onChange={(e) =>
                      setSelectedGroup({
                        ...selectedGroup,
                        groupName: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Select Metal */}
                <div className="mb-3 col-md-6">
                  <Form.Label>Metal</Form.Label>
                  <Form.Select
                    value={selectedGroup.metal}
                    className={buttonClass}
                    onChange={(e) =>
                      setSelectedGroup({
                        ...selectedGroup,
                        metal: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Metal</option>
                    <option value="Gold">Gold</option>
                    <option value="Silver">Silver</option>
                    <option value="Platinum">Platinum</option>
                    <option value="Diamond">Diamond</option>
                  </Form.Select>
                </div>
              </div>

              <div className="row">
                {/* Purity */}
                <div className="mb-3 col-md-6">
                  <Form.Label>Purity (C)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={selectedGroup.purity}
                    className={buttonClass}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^[0-9]*$/.test(val)) {
                        setSelectedGroup({
                          ...selectedGroup,
                          purity: val,
                        });
                      }
                    }}
                  />
                </div>

                {/* HSN Code */}
                <div className="mb-3 col-md-6">
                  <Form.Label>HSN Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedGroup.hsnCode}
                    className={buttonClass}
                    onChange={(e) =>
                      setSelectedGroup({
                        ...selectedGroup,
                        hsnCode: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="mb-3 col-md-6">
                  <Form.Label>Purity (%)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    pattern="^\d*\.?\d*$"
                    value={selectedGroup.touch || ""}
                    className={buttonClass}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*\.?\d*$/.test(val)) {
                        setSelectedGroup({
                          ...selectedGroup,
                          touch: val,
                        });
                      }
                    }}
                  />
                </div>

                                {/* Short Code */}
                <div className="mb-3 col-md-6">
                  <Form.Label>Short Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedGroup.shortCode}
                    className={buttonClass}
                    onChange={(e) =>
                      setSelectedGroup({
                        ...selectedGroup,
                        shortCode: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="row">


                {/* Status */}
                <div className="mb-3 col-md-6">
                  <Form.Label>Status</Form.Label>
                  <div className="d-flex">
                    <Form.Check
                      type="radio"
                      label="Active"
                      name="status"
                      value="1"
                      checked={String(selectedGroup.status) === "1"}
                      onChange={(e) =>
                        setSelectedGroup({
                          ...selectedGroup,
                          status: e.target.value,
                        })
                      }
                      className="me-3"
                    />
                    <Form.Check
                      type="radio"
                      label="Inactive"
                      name="status"
                      value="0"
                      checked={String(selectedGroup.status) === "0"}
                      onChange={(e) =>
                        setSelectedGroup({
                          ...selectedGroup,
                          status: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveChanges}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Metal Group Modal */}
      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        centered
        contentClassName={cardClass}
        // size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Metal Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form noValidate="" onSubmit={handleAddSubmit}>
            <div className="row">
              {/* Group Name */}
              <div className="mb-3 col-md-6">
                <label className="form-label">
                  Group Name 
                </label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  required
                  placeholder="Enter Group Name"
                  value={groupName}
                  onChange={handleGroupNameChange}
                />
              </div>

              {/* Select Metal */}
              <div className="mb-3 col-md-6">
                <label className="form-label">
                  Select Metal 
                </label>
                <select
                  className={`form-select ${buttonClass}`}
                  required
                  value={metal}
                  onChange={(e) => setMetal(e.target.value)}
                >
                  <option value="">Select Metal</option>
                  <option value="gold">Gold</option>
                  <option value="silver">Silver</option>
                  <option value="platinum">Platinum</option>
                  <option value="diamond">Diamond</option>
                </select>
              </div>
            </div>

            <div className="row">
              {/* Purity */}
              <div className="mb-3 col-md-6">
                <label className="form-label">
                  Purity (C) 
                </label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  required
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Enter Purity"
                  value={purity}
                  onChange={handlePurityChange}
                />
              </div>

              {/* Purity (%) */}
              <div className="mb-3 col-md-6">
                <label className="form-label">Purity (%)</label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  inputMode="decimal"
                  pattern="^\d*\.?\d*$"
                  placeholder="Enter Purity (%)"
                  value={purityPercent}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*\.?\d*$/.test(val)) setPurityPercent(val);
                  }}
                />
              </div>
            </div>

            <div className="row">
              {/* HSN Code */}
              <div className="mb-3 col-md-6">
                <label className="form-label">HSN Code</label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  placeholder="Enter HSN Code"
                  value={hsnCode}
                  onChange={(e) => setHsnCode(e.target.value)}
                />
              </div>
              {/* Short Code */}
              <div className="mb-3 col-md-6">
                <label className="form-label">
                  Short Code 
                </label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  required
                  placeholder="Enter Short Code"
                  value={shortCode}
                  onChange={(e) => setShortCode(e.target.value)}
                />
              </div>

              {/* Status */}
              {/* <div className="mb-3 col-md-6">
                <label className="form-label">Status</label>
                <div className="d-flex">
                  <Form.Check
                    type="radio"
                    label="Active"
                    name="addStatus"
                    value="active"
                    checked={status === "active"}
                    onChange={(e) => setStatus(e.target.value)}
                    className="me-3"
                  />
                  <Form.Check
                    type="radio"
                    label="Inactive"
                    name="addStatus"
                    value="inactive"
                    checked={status === "inactive"}
                    onChange={(e) => setStatus(e.target.value)}
                  />
                </div>
              </div> */}
            </div>

            {/* Submit Button */}
            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <button
                type="submit"
                className={`btn btn-primary ${buttonClass}`}
              >
                Submit
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default MetalGroupTable;
